import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { auth } from '../auth';

const APP_STORE_URL = 'https://apps.apple.com/app/mick-oc/id6772787701';

function AppDownloadLinks({ align = 'left' }) {
  const alignmentClass = align === 'center' ? 'items-center text-center' : 'items-start text-left';

  return (
    <div className={`flex flex-col gap-3 ${alignmentClass}`}>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">
        Mobile App
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[52px] items-center justify-center rounded-lg bg-brand-black px-5 py-3 text-base font-semibold text-white transition hover:bg-brand-black/90"
        >
          Download on the App Store
        </a>
        <span
          className="inline-flex min-h-[52px] cursor-not-allowed items-center justify-center rounded-lg border-2 border-stone-300 bg-white px-5 py-3 text-base font-semibold text-stone-500"
          aria-label="Google Play link coming soon"
          title="Google Play link coming soon"
        >
          Google Play coming soon
        </span>
      </div>
    </div>
  );
}

const howItWorks = [
  {
    title: 'Input Your Existing Playbook',
    body: 'Add the formations, motions, and plays your staff already has installed.',
  },
  {
    title: 'Use The App On Game Day',
    body: 'Open Mick OC on an iPhone or tablet, then speak or type the down, distance, and situation during the game. Mick OC will call one run and one pass from your playbook within seconds.',
  },
  {
    title: 'Review Every Call Afterward',
    body: 'Game-day calls, recommendations, and results are captured for post-game analysis, practice planning, and adjustments.',
  },
];

const benefits = [
  'Call plays faster with no more flipping through sheets',
  'Think clearly under pressure with fewer decisions and better focus',
  'Develop younger coaches faster with built-in decision support',
  'Maximize the playbook you already installed',
];

const roles = ['Head Coach', 'Offensive Coordinator', 'QB Coach', 'Play Caller at any level'];

const features = [
  'Private team playbook database',
  'Real-time run and pass play recommendations from your playbook',
  'Voice or manual input',
  'Mobile app for game day',
  'Web app for playbook management',
  'Coach account and team branding management',
  'Ability to create playbook-specific game plans',
  'Individual coaching points',
  'Continuous system improvements',
];

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await api.post('/auth/login', { email, password });

      if (res.data?.token) {
        auth.setToken(res.data.token);
        onLogin?.(res.data.token);
      } else {
        setError('Signed in, but no session token was returned.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not sign in. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-offwhite text-brand-black">
      <section className="border-b border-stone-200 bg-white px-4 py-6 text-brand-black md:py-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/mick-oc-logo.png" alt="Mick OC" className="h-auto w-44 rounded-lg bg-brand-black" />
            </div>
            <Link
              to="/signup"
              className="hidden rounded-lg bg-brand-orange px-4 py-3 text-sm font-semibold text-brand-black transition hover:bg-brand-orange/90 sm:inline-flex"
            >
              Start Team Account
            </Link>
            <Link to="/book-demo" className="rounded-lg border-2 border-brand-orange px-4 py-3 text-sm font-semibold text-brand-black transition hover:bg-brand-orange/10">Book a Demo</Link>
          </div>

          <div className="grid gap-8 py-10 lg:grid-cols-[1fr_24rem] lg:items-start lg:py-14">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-brand-orange">
                Real-time offensive coordinator assistant
              </p>
              <h1 className="mt-5 max-w-3xl text-5xl font-semibold leading-tight tracking-tight md:text-6xl">
                Make faster, smarter play calls from your own system.
              </h1>
              <p className="mt-5 max-w-2xl text-xl leading-9 text-stone-700">
                Mick OC instantly recommends plays from your own playbook based on the current
                game situation.
              </p>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-stone-600">
                No generic suggestions. No outside systems. Just your playbook optimized in real time.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/book-demo" className="inline-flex justify-center rounded-lg bg-brand-orange px-5 py-3 text-base font-semibold text-brand-black transition hover:bg-brand-orange/90">Book a Demo</Link>
                <Link
                  to="/signup"
                  className="inline-flex justify-center rounded-lg border border-stone-300 bg-white px-5 py-3 text-base font-semibold text-brand-black transition hover:bg-brand-offwhite"
                >
                  Input your Team's Playbook
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex justify-center rounded-lg border border-stone-300 bg-white px-5 py-3 text-base font-semibold text-stone-800 transition hover:bg-brand-offwhite"
                >
                  See How It Works
                </a>
              </div>

              <div className="mt-8">
                <AppDownloadLinks />
              </div>

              <div className="mt-9 grid max-w-2xl gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-stone-200 bg-brand-offwhite px-4 py-4">
                  <p className="text-2xl font-semibold text-brand-black">No generic</p>
                  <p className="mt-1 text-sm leading-6 text-stone-600">Calls come from your system</p>
                </div>
                <div className="rounded-lg border border-stone-200 bg-brand-offwhite px-4 py-4">
                  <p className="text-2xl font-semibold text-brand-black">Your system only</p>
                  <p className="mt-1 text-sm leading-6 text-stone-600">Recommendations stay inside your playbook.</p>
                </div>
                <div className="rounded-lg border border-stone-200 bg-brand-offwhite px-4 py-4">
                  <p className="text-2xl font-semibold text-brand-black">Real time</p>
                  <p className="mt-1 text-sm leading-6 text-stone-600">Optimized for the situation</p>
                </div>
              </div>
            </div>

            <aside className="rounded-lg border border-stone-200 bg-brand-offwhite p-6 text-brand-black shadow-sm md:p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-orange">
                Returning Coaches
              </p>
              <h2 className="mt-3 text-3xl font-semibold">Sign in to your team account</h2>
              <p className="mt-3 text-base leading-7 text-stone-700">
                Manage your playbook, test real-time recommendations, and review calls from your
                own offensive system.
              </p>

              <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                <div>
                  <label htmlFor="email" className="mb-2 block text-base font-medium text-stone-700">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field text-lg"
                    autoComplete="email"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-base font-medium text-stone-700">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field text-lg"
                    autoComplete="current-password"
                    required
                  />
                </div>

                {error && (
                  <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
                    {error}
                  </p>
                )}

                <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-base">
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              <div className="mt-6 border-t border-stone-200 pt-5 text-base text-stone-700">
                New team?{' '}
                <Link to="/signup" className="font-semibold text-brand-orange transition hover:text-brand-orange">
                  Start Calling Better Plays Today
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <main>
        <section className="px-4 py-14">
          <div className="mx-auto max-w-4xl">
            <p className="eyebrow">What This Is</p>
            <h2 className="mt-3 text-4xl font-semibold">A real-time offensive coordinator assistant.</h2>
            <p className="mt-5 text-xl leading-9 text-stone-700">
              Mick OC helps you make faster, smarter play calls by instantly recommending plays
              from your own system based on the current game situation.
            </p>
            <div className="mt-7 grid gap-3 text-lg font-semibold text-brand-black sm:grid-cols-3">
              <div className="rounded-lg border border-stone-200 bg-white px-4 py-4">No generic suggestions.</div>
              <div className="rounded-lg border border-stone-200 bg-white px-4 py-4">No outside systems.</div>
              <div className="rounded-lg border border-stone-200 bg-white px-4 py-4">
                Just your playbook - optimized in real time.
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-stone-200 bg-white px-4 py-14">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="eyebrow">Official Production Site</p>
              <h2 className="mt-3 text-4xl font-semibold">Run your Mick OC team account from mickoc.com.</h2>
              <p className="mt-5 text-lg leading-8 text-stone-700">
                Coaches use the website as the production entry point to create the team account,
                enter the playbook, manage coach access, upload the team logo or mascot, and review
                called plays after practices and games.
              </p>
              <Link
                to="/signup"
                className="mt-7 inline-flex rounded-lg bg-brand-orange px-5 py-3 text-base font-semibold text-brand-black transition hover:bg-brand-orange/90"
              >
                Create Team Account
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                'Create the team account',
                'Enter the offensive playbook',
                'Manage coach logins',
                'Upload logo or mascot',
                'Review called plays',
                'Spot-check recommendations',
              ].map((item) => (
                <div key={item} className="rounded-lg border border-stone-200 bg-brand-offwhite px-5 py-5 text-base font-semibold">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="bg-white px-4 py-14">
          <div className="mx-auto max-w-6xl">
            <p className="eyebrow">How It Works</p>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {howItWorks.map((step, index) => (
                <article key={step.title} className="rounded-lg border border-stone-200 bg-brand-offwhite px-5 py-6">
                  <p className="text-sm font-semibold text-brand-orange">Step {index + 1}</p>
                  <h3 className="mt-3 text-2xl font-semibold">{step.title}</h3>
                  <p className="mt-3 text-base leading-7 text-stone-700">{step.body}</p>
                </article>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-stone-200 bg-brand-offwhite px-6 py-6">
              <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <h3 className="text-3xl font-semibold text-brand-black">Use Mick OC on game day.</h3>
                  <p className="mt-3 max-w-3xl text-base leading-7 text-stone-700">
                    Manage the playbook on the website, then use the mobile app from the sideline to speak
                    or type the current situation and review the recommended run and pass.
                  </p>
                </div>
                <AppDownloadLinks />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-stone-200 bg-white px-4 py-14 text-brand-black">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-orange">
                Game Day Experience
              </p>
              <h2 className="mt-3 text-4xl font-semibold">This is what it feels like on the sideline.</h2>
              <p className="mt-5 text-lg leading-8 text-stone-700">
                It is 3rd and 4. Ball on the +35. The defense is shifting late. On your iPhone or
                tablet, Mick OC gives you the best run and pass play from your own playbook based
                on the current situation.
              </p>
            </div>

            <div className="rounded-lg border border-stone-200 bg-brand-offwhite px-6 py-6 text-brand-black">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">
                Within Seconds
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-brand-offwhite px-4 py-4">
                  <p className="form-label">Run</p>
                  <p className="mt-2 text-2xl font-semibold">Trips Right Y Jet Power 25</p>
                </div>
                <div className="rounded-lg bg-brand-offwhite px-4 py-4">
                  <p className="form-label">Pass</p>
                  <p className="mt-2 text-2xl font-semibold">Ace Gun 1881</p>
                </div>
              </div>
              <p className="mt-5 text-base leading-7 text-stone-700">
                Both aligned with your playbook. Both ready to call. Afterward, the call is there
                for film review, practice planning, and adjustments.
              </p>
            </div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto max-w-6xl">
            <p className="eyebrow">What This Does For You</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {benefits.map((benefit) => (
                <div key={benefit} className="rounded-lg border border-stone-200 bg-white px-5 py-5 text-base font-semibold">
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-14">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
            <div>
              <p className="eyebrow">Built For Real Coaches</p>
              <h2 className="mt-3 text-4xl font-semibold">An edge when the game speeds up.</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {roles.map((role) => (
                  <div key={role} className="rounded-lg bg-brand-offwhite px-4 py-4 text-base font-semibold">
                    {role}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="eyebrow">What You Get</p>
              <div className="mt-6 space-y-3">
                {features.map((feature) => (
                  <div key={feature} className="rounded-lg border border-stone-200 px-4 py-4 text-base font-semibold">
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-14">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <p className="eyebrow">Your System Stays Yours</p>
              <h2 className="mt-3 text-4xl font-semibold">No generic suggestions. No outside systems.</h2>
              <p className="mt-5 text-lg leading-8 text-stone-700">
                Your playbook stays private, and every recommendation is based on the system your
                staff already teaches.
              </p>
            </div>

            <div className="rounded-lg border border-stone-200 bg-white px-6 py-6">
              <p className="eyebrow">Pricing</p>
              <h2 className="mt-3 text-4xl font-semibold">One Team. One System. One Price.</h2>
              <p className="mt-5 text-5xl font-semibold">$999.00 US</p>
              <p className="mt-2 text-lg text-stone-700">per team, per year</p>
              <div className="mt-6 space-y-3 text-base font-semibold text-stone-800">
                <p>Unlimited plays</p>
                <p>Unlimited coaches</p>
                <p>Full access to the recommendation engine</p>
              </div>
              <Link
                to="/signup"
                className="mt-7 inline-flex rounded-lg bg-brand-orange px-5 py-3 text-base font-semibold text-brand-black transition hover:bg-brand-orange/90"
              >
                Start Building Your Playbook Today
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-brand-orange px-4 py-14 text-brand-black">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-4xl font-semibold">You already have the plays.</h2>
            <p className="mt-4 text-xl leading-8">
              Now get the real-time assistant that helps you call them.
            </p>
            <Link
              to="/signup"
              className="mt-8 inline-flex rounded-lg bg-white px-6 py-3 text-base font-semibold text-brand-black transition hover:bg-brand-offwhite"
            >
              Start Calling Better Plays Today
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200 bg-white px-4 py-8 text-center text-sm font-semibold text-stone-600">
        <p>Mick OC - Your Real-Time Offensive Coordinator Assistant</p>
        <div className="mt-3 flex justify-center gap-4">
          <Link to="/privacy" className="hover:text-brand-orange">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-brand-orange">
            Terms
          </Link>
          <Link to="/support" className="hover:text-brand-orange">
            Support
          </Link>
        </div>
      </footer>
    </div>
  );
}
